// crewbit-admin/src/components/schedule/DayEmptySlot.tsx
//
// Schedule Module Phase C C2 — empty-state CTA inside an empty <WeekDayColumn>
// (or empty Day cell in <MonthCalendar> when C3 ships). Compact affordance —
// "+ New" button that triggers the caller's onCreate handler.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §3.3 + §3.6.

import { Plus } from 'lucide-react';
import { palette, admin } from '../../styles/tokens';

export interface DayEmptySlotProps {
  date: string;
  /** Optional click handler. When omitted, slot renders as passive placeholder. */
  onCreate?: (date: string) => void;
  density?: 'compact' | 'normal';
}

export function DayEmptySlot({ date, onCreate, density = 'compact' }: DayEmptySlotProps) {
  const isCompact = density === 'compact';
  const passive = !onCreate;

  return (
    <button
      type="button"
      onClick={passive ? undefined : () => onCreate(date)}
      disabled={passive}
      aria-label={passive ? 'No entries' : `Add entry on ${date}`}
      style={{
        background: 'transparent',
        border: `1px dashed ${admin.border}`,
        borderRadius: 8,
        padding: isCompact ? '12px 8px' : '24px 16px',
        cursor: passive ? 'default' : 'pointer',
        color: palette.g500,
        fontSize: isCompact ? 11 : 13,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minHeight: isCompact ? 56 : 80,
        width: '100%',
      }}
    >
      {passive ? null : <Plus size={isCompact ? 12 : 14} />}
      {passive ? 'No entries' : 'New'}
    </button>
  );
}
